/**
 * Purchase options — the one-time / subscribe choice on the PDP.
 *
 * Three jobs:
 *   · reveal the cadence panel when the subscription card is chosen,
 *   · keep the radio's value in step with the cadence select,
 *   · re-price both cards when the variant changes.
 *
 * The radios are named `selling_plan`, which is what Shopify's product form
 * reads, so Horizon's own buy buttons submit the right plan with no extra work.
 *
 * Horizon updates its price element on a variant switch but does not re-render
 * this block, so without the last job the card quoted the first variant's price
 * AND submitted the first variant's plan id.
 */
class BunchberryPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.detail = this.querySelector('[data-bb-detail]');
    this.subRadio = this.querySelector('[data-bb-sub]');
    this.cadence = this.querySelector('[data-bb-cadence]');
    this.subWrap = this.querySelector('.bb-purchase__sub');
    this.subCta = this.getAttribute('data-sub-cta') || '';
    this.plans = this.#planMap();

    this.addEventListener('change', (event) => {
      if (event.target.name === 'selling_plan') this.#sync();
      // Choosing a cadence picks a different plan within the same group.
      if (event.target === this.cadence && this.subRadio) {
        this.subRadio.value = this.cadence.value;
        this.subRadio.checked = true;
        this.#sync();
      }
    });

    // Horizon's variant picker announces the new variant on the document.
    this.onSelect = (e) => {
      this.#variant(e.detail?.variantId);
      // The buy buttons are replaced asynchronously, after this event. Re-apply
      // the label across the following few frames so the swap cannot land on
      // top of it — cheap, and it converges the moment the new button exists.
      [0, 150, 400, 800].forEach((ms) => setTimeout(() => this.#label(), ms));
    };
    document.addEventListener('shopify:product:select', this.onSelect);

    this.#watchButton();
    this.#sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('shopify:product:select', this.onSelect);
  }

  #planMap() {
    const node = this.querySelector('script[data-bb-plan-map]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  /** Re-price both cards, and re-point the plan ids, for the chosen variant. */
  #variant(id) {
    if (!id || !this.plans) return;
    const data = this.plans[String(id)];
    if (!data) return;

    const onetime = this.querySelector('.bb-purchase__price');
    if (onetime) onetime.textContent = data.price;

    const plans = data.plans ?? [];

    // A variant can sit outside the plan even when its siblings are in it.
    // Offering a subscription that variant cannot fulfil is worse than
    // offering none, so the whole card goes.
    if (this.subWrap) this.subWrap.hidden = plans.length === 0;
    if (plans.length === 0) {
      const onetimeRadio = this.querySelector('input[name="selling_plan"][value=""]');
      if (onetimeRadio) onetimeRadio.checked = true;
      this.#sync();
      return;
    }

    // Keep the customer's chosen cadence if it still exists for this variant.
    let index = 0;
    if (this.cadence) {
      const wanted = this.cadence.selectedIndex;
      index = wanted >= 0 && wanted < plans.length ? wanted : 0;
      [...this.cadence.options].forEach((opt, i) => {
        if (plans[i]) {
          opt.value = plans[i].id;
          opt.textContent = plans[i].name;
          opt.hidden = false;
        } else {
          opt.hidden = true;
        }
      });
      this.cadence.selectedIndex = index;
    }

    const chosen = plans[index];
    if (this.subRadio) this.subRadio.value = chosen.id;

    const sale = this.querySelector('.bb-purchase__sale');
    if (sale) sale.textContent = chosen.price;
    const was = this.querySelector('.bb-purchase__was');
    if (was) {
      const discounted = chosen.price !== data.price;
      was.hidden = !discounted;
      was.textContent = data.price;
    }

    this.#sync();
  }

  /**
   * Every add-to-cart button on the page. There are two: the one in the buy
   * box and the one in the sticky bar, and both must read the same thing.
   */
  #buttons() {
    const scope =
      this.closest('.shopify-section, product-information') ?? this.ownerDocument;
    return [
      ...scope.querySelectorAll('.add-to-cart-button'),
      ...this.ownerDocument.querySelectorAll('.sticky-add-to-cart__button'),
    ].filter((b, i, all) => all.indexOf(b) === i);
  }

  /**
   * The label is a text node inside .add-to-cart-text, sitting beside the cart
   * icon — not a direct child of the button, which is where this used to look.
   */
  #labelNode(btn) {
    const holder = btn.querySelector('.add-to-cart-text') ?? btn;
    // The buy box holds its label as a direct child; the sticky bar nests it
    // deeper. Walk for the first text node with content, skipping the icon so
    // the label never lands inside the SVG.
    const walker = this.ownerDocument.createTreeWalker(holder, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.textContent.trim()) return NodeFilter.FILTER_REJECT;
        if (node.parentElement?.closest('svg')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    return walker.nextNode();
  }

  /**
   * Horizon re-renders the buy buttons on variant change, which discards our
   * label. Rather than fight it, re-apply whenever the button's contents
   * change — guarded so our own write does not retrigger the observer.
   */
  /**
   * Watch a stable ancestor, not the buttons themselves.
   *
   * Horizon does not re-render the buy buttons in place on a variant change —
   * it replaces the elements. An observer bound to the old button silently
   * stops applying, which is why the label reverted to "Add to cart" the moment
   * a subscriber picked a different size.
   */
  #watchButton() {
    if (!this.subCta) return;
    const scope =
      this.closest('.shopify-section, product-information') ?? this.ownerDocument.body;
    if (!scope) return;
    this.observer = new MutationObserver(() => {
      if (this.writing) return;
      this.#label();
    });
    this.observer.observe(scope, { childList: true, characterData: true, subtree: true });
  }

  #label() {
    if (!this.subCta) return;
    const subscribing = this.subRadio?.checked && this.subWrap?.hidden !== true;

    this.writing = true;
    for (const btn of this.#buttons()) {
      const node = this.#labelNode(btn);
      if (!node) continue;
      // Remember each button's own wording — the sticky bar's may differ.
      if (!this.originals) this.originals = new WeakMap();
      if (!this.originals.has(btn)) this.originals.set(btn, node.textContent);

      const wanted = subscribing ? this.subCta : this.originals.get(btn);
      if (node.textContent.trim() !== wanted.trim()) node.textContent = wanted;
    }
    requestAnimationFrame(() => { this.writing = false; });
  }

  #sync() {
    if (this.detail) this.detail.hidden = !this.subRadio?.checked;
    this.#label();
  }
}

if (!customElements.get('bunchberry-purchase-options')) {
  customElements.define('bunchberry-purchase-options', BunchberryPurchaseOptions);
}
